// components/FilterComponent.jsx (Modernized)
import React, { useEffect } from 'react';
import { ButtonToolbar } from 'ui';
import { useToasts } from 'ui/toast';
import useFilterData from 'hooks/useFilterData';
import CodesFilter from './filters/CodesFilter';
import TranscriptsFilter from './filters/TranscriptsFilter';
import DemographicFilters from './filters/DemographicFilters';
import SurveysFilter from './filters/SurveysFilter';
import { useFilterState } from 'contexts/FilterStateContext';
import { useInvalidateFilteredExcerpts } from 'hooks/excerpts/queries';

const FilterComponent = ({
  projectID,
  dropdownAbove,
  hideDescriptors,
  codeFilterButtonText,
  codeSubHeader,
  transcriptFilterButtonText,
  transcriptSubHeader,
  hideSurveys,
  source = 'snippets',
}) => {
  // Get toast functionality
  const { addToast } = useToasts();

  // Get filter state and actions from the lightweight context
  const {
    filters,
    toggleCode,
    toggleTranscript,
    toggleSurvey,
    toggleDemographic,
    clearCodes,
    clearTranscripts,
    clearSurveys,
    clearDemographic,
    setOperation
  } = useFilterState();

  // Get the invalidate function for this source
  const invalidateFilteredExcerpts = useInvalidateFilteredExcerpts(projectID, filters, source);

  // Use the custom hook to fetch all the data we need
  const { 
    codes, 
    surveys, 
    project, 
    filterReport, 
    descriptorsData, 
    isLoading,
    error 
  } = useFilterData(projectID, filters, source);

  // Handle errors with toast notification
  useEffect(() => {
    if (error) {
      addToast('Something went wrong. Please refresh and try again.', { 
        appearance: 'error',
        autoDismiss: true
      });
    }
  }, [error, addToast]);

  // Show loading state if any data is still loading
  if (isLoading) {
    return null; // or loading spinner
  }

  // Get transcripts from project data
  const transcripts = project?.transcripts || [];

  return (
    <ButtonToolbar>
      <CodesFilter
        codes={codes}
        filters={filters}
        filterReport={filterReport}
        onToggleCode={toggleCode}
        onClearCodes={clearCodes}
        onSetOperation={setOperation}
        onApplyFilter={invalidateFilteredExcerpts}
        dropdownAbove={dropdownAbove}
        codeFilterButtonText={codeFilterButtonText}
        codeSubHeader={codeSubHeader}
        large
      />

      <TranscriptsFilter 
        transcripts={transcripts}
        filters={filters}
        filterReport={filterReport}
        onToggleTranscript={toggleTranscript}
        onClearTranscripts={clearTranscripts}
        onApplyFilter={invalidateFilteredExcerpts}
        dropdownAbove={dropdownAbove}
        transcriptFilterButtonText={transcriptFilterButtonText}
        transcriptSubHeader={transcriptSubHeader}
      />

      {!hideDescriptors && (
        <DemographicFilters 
          descriptorsData={descriptorsData}
          filters={filters}
          filterReport={filterReport}
          onToggleDemographic={toggleDemographic}
          onClearDemographic={clearDemographic}
          onApplyFilter={invalidateFilteredExcerpts}
          dropdownAbove={dropdownAbove}
        />
      )}

      {!hideSurveys && (
        <SurveysFilter 
          surveys={surveys}
          filters={filters}
          filterReport={filterReport}
          onToggleSurvey={toggleSurvey}
          onClearSurveys={clearSurveys}
          onApplyFilter={invalidateFilteredExcerpts}
          dropdownAbove={dropdownAbove}
        />
      )}
    </ButtonToolbar>
  );
}

export default FilterComponent;